import React, { useEffect, useMemo, useState, Suspense } from 'react';
import { PromiseState } from 'react-refetch';
import delve from 'dlv';
import { useHistory } from 'react-router';
import isEqual from 'lodash.isequal';
import { useDispatch } from 'react-redux';

import { logToAmplitude } from 'helpers/logAmplitudeEvent';
import useExportURL from 'hooks/useExportURL';
import useSearch from 'hooks/useSearch';
import useOffset from 'hooks/useOffset';
import useBrowseTableHeaders from 'components/data-containers/BrowseEntities/shared/useBrowseTableHeaders';
import { loadRecentHeaders } from 'redux/reducers/user/browseTable';
import usePrepareCustomViewHeaders from 'components/data-containers/BrowseEntities/shared/usePrepareCustomViewHeaders';

import BrowseTable from 'components/data-containers/BrowseEntities/BrowseTable/BrowseTable';
import CustomListModal from 'components/customlists/CustomListModal';
import BrowseEntitiesSkeleton from 'components/data-containers/BrowseEntities/SkeletonLoader';

import { IBrowseCustomList } from './shared';
import { SalesTypeValues } from 'maps/sales-types';
import { IMetric } from 'maps/metrics';
import { ILabelValuePair } from 'types/shared';
import { TopicFilterNames } from 'components/TopicFilters/shared';
import {
  ICustomListData,
  ICustomList,
} from 'components/customlists/AddToList/shared';
import { TModalContent } from 'components/customlists/CustomListPageHeader';
import { TTableHeader } from 'components/CustomizeView/CustomizeView';

interface IViewListProps {
  allowedFilters: TopicFilterNames[];
  customListMetadata: PromiseState<ICustomList>;
  customListInvitationData: PromiseState<any>;
  deleteCustomEntityList: (listId: string, next: () => void) => any;
  fetchCustomListInvitation: ({ listId }: { listId: string }) => any;
  leaveCustomEntityList: (
    listId: string,
    userId: string,
    close: () => void
  ) => void;
  list: PromiseState<IBrowseCustomList>;
  isLargeScreen: boolean;
  isExternalUser: boolean;
  putCustomEntityList: (list: ICustomListData, close: () => void) => void;
  selectedSalesType: ILabelValuePair<SalesTypeValues>;
  setNameFilter: (searchValue: string) => void;
  saveComponentSettings: (settings: {
    selectedSalesType: string;
    browseEntityType: string;
  }) => void;
  sortMetric: IMetric;
  sortOrder: string;
  setSortMetric: any;
  setSortOrder: any;
  changeSortMetric: any;
  switchSortOrder: any;
  setLimit: any;
  setOffsetState: any;
  saveToApi: (headers: TTableHeader[]) => void;
  userId: string;
  isSameFilters: boolean;
  totalLimit: number;
  globalFilters?: any;
}

/*
 * Variation of the browse table shows all the entities in a custom list.
 * A loading state (TableRowLoader) is shown while the data is being retrieved.
 * Table headers are only rendered in a desktop view.
 */
const ViewList: React.FC<IViewListProps> = (props) => {
  const {
    allowedFilters,
    customListMetadata,
    customListInvitationData,
    deleteCustomEntityList,
    fetchCustomListInvitation,
    leaveCustomEntityList,
    list = PromiseState.create<IBrowseCustomList>(),
    isLargeScreen,
    isExternalUser,
    putCustomEntityList,
    selectedSalesType,
    setNameFilter,
    saveComponentSettings,
    sortMetric,
    sortOrder,
    setSortMetric,
    setSortOrder,
    changeSortMetric,
    switchSortOrder,
    setLimit,
    setOffsetState,
    saveToApi,
    userId,
    isSameFilters,
    totalLimit,
    globalFilters,
  } = props;

  const tableType = 'customList';
  const { fulfilled, pending, rejected, reason } = list;
  const listData = list.value;
  const customListName = listData?.displayName;
  const history = useHistory();
  const dispatch = useDispatch();

  // `useOffset` concats or invalidates the entity data when subsequent
  // API calls are made. For instance, when the user clicks "Show 10 more"
  // this hook will make sure the entities are added to the previous ones.
  // When we switch the sort metric, order, or filters — invalidation makes sure
  // the previous entities are flushed.
  const increment = 100;
  const {
    offsetState,
    setOffset,
    setEntities,
    setInvalidate,
    setValidate,
    resetOffset,
  } = useOffset({
    limit: totalLimit,
    increment,
    entitiesKey: 'entities',
  });
  const { limit, offset, value } = offsetState;
  const entityCount = delve(listData, 'totalEntityCount');
  const entities = delve(value, 'entities');
  const exportUrl = useExportURL(list, {
    offset: 0,
    limit: limit + offset,
  });

  const { searchValue, inputValue, handleSearch } = useSearch();
  const [showModal, setShowModal] = useState(false);
  const hideModal = (): void => {
    setShowModal(false);
  };
  const [modalContent, setModalContent] = useState<TModalContent>('EDIT_LIST');
  const handleListInfo = (modalAction: TModalContent): void => {
    setModalContent(modalAction);
    setShowModal(true);
  };

  const [isDropdownShown, setDropdownShown] = useState(false);
  const toggleDropdown = (): void => {
    setDropdownShown(!isDropdownShown);
    if (!isDropdownShown) {
      logToAmplitude('Custom View Button Clicked', { type: 'List' });
    }
  };

  const { availableHeaders, savedHeaders, formatHeaders } =
    usePrepareCustomViewHeaders(tableType);

  const hasDefaultHeaders = useMemo(
    () =>
      savedHeaders &&
      savedHeaders?.length > 0 &&
      availableHeaders.every((defaultItem: any) =>
        savedHeaders.find((header: any) => header.value === defaultItem.value)
      ),
    [availableHeaders, savedHeaders]
  );
  const customizeButtonText = useMemo(
    () => (hasDefaultHeaders ? 'Customize View' : 'Table has been customized'),
    [savedHeaders]
  );

  useEffect(() => {
    setNameFilter(searchValue);
  }, [searchValue, setNameFilter]);

  useEffect(() => {
    setLimit(limit);
    setOffsetState(offset);
  }, [limit, offset, setLimit, setOffsetState]);

  /*
   * Pass the entity data to the `useOffset` hook.
   */
  useEffect(() => {
    const isEqualEntities = isEqual(entities, list?.value?.entities);
    if (list?.value?.entities && (!isEqualEntities || !isSameFilters)) {
      setEntities({
        entities: list.value.entities,
        totalEntityCount: list.value.totalEntityCount,
      });
    }
  }, [list, setEntities, entities, isSameFilters]);

  const showMore = (): void => {
    setValidate();
    setOffset();
    setInvalidate();
  };

  useEffect(
    () => resetOffset(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /*
   * When an interaction took place that will trigger a new API call, we want to
   * clear the entities in state. This is because the API should compute that and
   * the entities in state are invalid with the upcoming state.
   */
  useEffect(() => {
    // `setInvalidate` flushes the entities when the next batch of entities arrive.
    // Happens at the next `setEntities` so we don't see a skeleton loader in between.
    setInvalidate();
  }, [
    setInvalidate,
    sortMetric,
    sortOrder,
    selectedSalesType,
    searchValue,
    isSameFilters,
  ]);

  const extraToolbarItems = list?.value?.id
    ? [
        {
          href: exportUrl,
          label: 'Export data',
        },
        {
          onClick: (): void => history.push(`/list/${list?.value?.id}/edit`),
          label: 'Edit list',
        },
        {
          onClick: (): void => handleListInfo('EDIT_LIST'),
          label: 'List information',
        },
        {
          onClick: (): void => handleListInfo('LEAVE_LIST'),
          label: 'Leave list',
        },
        {
          onClick: (): void => handleListInfo('DELETE_LIST'),
          label: 'Delete list',
        },
      ]
    : [];
  const { mobileHeaders, tableHeaders } = useBrowseTableHeaders({
    browseEntityType: 'item',
    tableHeadersType: 'standard',
    tableType,
  });
  const customSort = {
    key: 'custom',
    label: 'Custom',
    sortOrder: '',
    value: 'custom',
  };
  const mobileTableHeaders = [customSort, ...mobileHeaders];
  const activeItem = sortMetric
    ? { ...sortMetric, withoutSubLabel: true }
    : customSort;

  useEffect(() => {
    const headers = isLargeScreen ? tableHeaders : mobileHeaders;
    const selectedHeader = headers.find(
      (header: any) => header.value === sortMetric?.value
    );
    if (sortMetric && sortMetric?.value !== 'custom' && !selectedHeader) {
      setSortMetric(customSort);
    }
  }, [
    customSort,
    mobileHeaders,
    tableHeaders,
    setSortMetric,
    isLargeScreen,
    sortMetric,
  ]);

  /** Get the latest table headers for BrowseOpus table */
  useEffect(() => {
    dispatch(loadRecentHeaders(tableType));
  }, [dispatch]);

  return (
    <>
      <Suspense
        fallback={
          <BrowseEntitiesSkeleton
            title="List"
            browseEntityType={tableType}
            enableSearch
            isBrowseTable
            showTabs={false}
          />
        }
      >
        <BrowseTable
          dataCy="browse-opus-customList"
          extraToolbarItems={extraToolbarItems}
          genericTitle={customListName}
          glossarySlug="lists/browse"
          tableName="customLists"
          error={rejected}
          loading={pending}
          reason={reason}
          success={fulfilled}
          activeItem={activeItem}
          allowedFilters={allowedFilters}
          dropdownItems={mobileTableHeaders}
          entityCount={entityCount}
          entities={entities}
          entityType={'in List'}
          inputValue={inputValue}
          isExternalUser={isExternalUser}
          isLargeScreen={isLargeScreen}
          offset={increment}
          salesType={selectedSalesType}
          searchValue={searchValue}
          showDataWarning={false}
          sortMetric={sortMetric}
          sortOrder={sortOrder}
          tableHeaders={tableHeaders}
          changeSortMetric={changeSortMetric}
          handleSearch={handleSearch}
          handleToggle={switchSortOrder}
          saveComponentSettings={saveComponentSettings}
          setOffset={showMore}
          setSortMetric={setSortMetric}
          setSortOrder={setSortOrder}
          globalFilters={globalFilters}
          customizedMode={{
            isShownCustomizeButton: true,
            onClick: toggleDropdown,
            customizeButtonText,
            isCustomizedActive: !hasDefaultHeaders,
            saveToApi: saveToApi,
            savedItems: savedHeaders,
            allAvailableItems: availableHeaders,
            canTurnOffAll: false,
            isCustomizedDropdownShown: isDropdownShown,
            formatItems: formatHeaders,
          }}
        />
        {showModal && customListMetadata?.value && (
          <CustomListModal
            customList={customListMetadata.value}
            customListInvitationData={customListInvitationData}
            customListMetadata={customListMetadata}
            deleteCustomEntityList={deleteCustomEntityList}
            fetchCustomListInvitation={fetchCustomListInvitation}
            hideModal={hideModal}
            leaveCustomEntityList={leaveCustomEntityList}
            modalContent={modalContent}
            putCustomEntityList={putCustomEntityList}
            saveToApi={saveToApi}
            userId={userId}
          />
        )}
      </Suspense>
    </>
  );
};

export default ViewList;
